import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { SearchAutocomplete as RSKSearchAutoComplete, SearchStats } from '@uncinc/react-searchkit';
import type { SearchAutocompleteRenderProps as RenderProps } from '@uncinc/react-searchkit/dist/components/Autocomplete';

import { SEARCHKIT_AUTOCOMPLETE_DEBOUNCE_TIME } from '../../../config/constants';
import SearchBar, { type SearchBarProps } from '../Bar';

import './style.scss';

export interface SearchAutocompleteProps extends RenderProps {
  searchbarProps?: SearchBarProps;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = (props) => {
  const {
    searchbarProps,
    ...restProps
  } = props;

  const { t } = useTranslation();

  const classNames = c(
    'SearchAutocomplete',
    (searchbarProps?.type && `SearchAutocomplete--${searchbarProps.type}`),
  );

  return (
    <div className={classNames}>
      <RSKSearchAutoComplete.DefaultChild
        {...restProps}
        autoCompleteDebounce={SEARCHKIT_AUTOCOMPLETE_DEBOUNCE_TIME}
        renderInputComponent={(inputProps) => {
          // The fix below is needed to react-autowhatever
          // Which updates the focus of the element on an onBlur
          // However on onBlur, the button is rerendered
          // So we need a slight delay in the blur
          const { onBlur, onSubmit } = inputProps;
          const inputOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            if (onBlur && typeof onBlur === 'function') {
              setTimeout(() => {
                onBlur(event);
              }, 100);
            }
          };

          // We reshape the onSubmit event handler here to handle both
          // inputProps and searchbarProps submit functions, so we both
          // refine the search within the autocomplete context, as well
          // as the outer SearchAllContent context.
          const inputOnSubmit = () => {
            if (onSubmit && typeof onSubmit === 'function') {
              // @ts-expect-error
              // Typescript wants us to add 'event' as argument here,
              // but we don't have access to the event parameter in this context.
              onSubmit();
            }

            if (searchbarProps?.onSubmit) {
              searchbarProps.onSubmit(inputProps.value);
            }
          };

          // eslint-disable-next-line no-param-reassign
          inputProps.onBlur = inputOnBlur;
          // eslint-disable-next-line no-param-reassign
          inputProps.onSubmit = inputOnSubmit;

          return (
            <>
              <SearchBar
                {...searchbarProps}
                {...inputProps}
                type={searchbarProps?.type}
              />
              <div className="SearchAutocomplete__Stats__Wrapper">
                <div className="SearchAutocomplete__Stats">
                  <p>{t('searchkit.autocomplete.title')}</p>
                  <SearchStats />
                </div>
              </div>
            </>
          );
        }}
      />
    </div>
  );
};

export default SearchAutocomplete;
